import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Rating,
  Grid,
  Tooltip,
  FormControlLabel,
  Checkbox, 
  Avatar,
  Stack  
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonOutline as PersonOutlineIcon,
  SmartToyOutlined as SmartToyIcon,
  Star as StarIcon,
  Share as ShareIcon,
  FileDownload as FileDownloadIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Reply as ReplyIcon,
  Chat as ChatIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Article as ArticleIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  addDoc,
  serverTimestamp,
  getDocs,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle } from 'docx';
import ShareModal from './ShareModal';
import CreateAsanaTaskModal from './CreateAsanaTaskModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BEPMemoDisplay from './DecisionMemo/BEPMemoDisplay';
import ArtifactReviewPanel from './ArtifactReviewPanel';
import { generateGovernanceHeader, generateGovernanceFooter } from '../utils/governanceExport';

const SearchInput = React.memo(({ onSearch, onClear }) => {
    const [inputValue, setInputValue] = React.useState('');
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSearch(inputValue);
        }
    };
    
    const handleClear = () => {
        setInputValue('');
        onClear();
    };

    const handleSearchClick = () => {
        onSearch(inputValue);
    };
    
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
                placeholder="Search and press Enter..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                fullWidth
                InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    endAdornment: inputValue && (
                        <IconButton size="small" onClick={handleClear}>
                            <ClearIcon />
                        </IconButton>
                    )
                }}
            />
            <Button
                variant="contained"
                onClick={handleSearchClick}
                size="small"
                disabled={!inputValue.trim()}
                sx={{ height: '40px', minWidth: '80px' }}
            >
                Search
            </Button>
        </Box>
    );
});

const ExistingQuestions = ({
clientId,
user,
clientIsActive,
clients,
onQuestionUpdate,
userAsanaConnection,
forwardedSearchQuery,
orgConfig, // NEW: Organization config for BEP detection
configLoading // NEW: Loading state
}) => {
  console.log('[DEBUG] USER ExistingQuestions component loaded with props:', { clientId, user: user?.email });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingFullHistory, setIsLoadingFullHistory] = useState(false);
  const [showLoadingNote, setShowLoadingNote] = useState(false);
  // Time-boxed default: 90 days
  const DEFAULT_DAYS_BACK = 90;
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [isAsanaModalOpen, setIsAsanaModalOpen] = useState(false);
  const [selectedItemForAsana, setSelectedItemForAsana] = useState(null);
  const buFollowUpRef = useRef(null);
  const followUpRef = useRef(null);

// NEW: Smart helper function to implement the fallback hierarchy
const isAsanaAvailableForItem = (item) => {
  if (!item) return false;

  // 1. Check for a client-level connection first
  const client = clients.find(c => c.id === item.clientId);
  if (client?.asanaConnection?.status === 'connected') {
    return true;
  }

  // 2. If no client connection, fall back to the user-level connection
  if (userAsanaConnection?.status === 'connected') {
    return true;
  }

  // 3. If neither exists, Asana is not available
  return false;
};
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedItemForShare, setSelectedItemForShare] = useState(null);
  const [selectedItemForNote, setSelectedItemForNote] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFields, setExportFields] = useState({
    questionId: false,
    clientName: true,
    questionText: true,
    answerText: true,
    dateCreated: true,
    feedbackRating: false,
    feedbackText: false,
    userNotes: false,
    includeFollowUps: true,
    bu_title: true,
    bu_content: true,
    bu_sources: true
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  // Search and filter states
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [localSortOption, setLocalSortOption] = useState('newest');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Follow-up states
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [selectedQuestionForFollowUp, setSelectedQuestionForFollowUp] = useState(null);
  const [submittingFollowUp, setSubmittingFollowUp] = useState(false);
  const [buFollowUpDialogOpen, setBuFollowUpDialogOpen] = useState(false);
  const [selectedUpdateForFollowUp, setSelectedUpdateForFollowUp] = useState(null);
  const [submittingBuFollowUp, setSubmittingBuFollowUp] = useState(false);

  // Feedback states
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedItemForFeedback, setSelectedItemForFeedback] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Helper function to highlight search terms
  // Helper function to highlight search terms
const highlightSearchTerms = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(${escaped})`, 'gi'); // capturing group
  const parts = text.split(re);

  // In a split() with a capturing group, odd indices are the matches
  return parts.map((part, i) =>
    i % 2 === 1
      ? <span key={i} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span>
      : <span key={i}>{part}</span>
  );
};

  // Helper to process React children and apply highlighting to text nodes
const processTextChildren = (children, searchTerm) => {
  if (!searchTerm) return children;
  return React.Children.map(children, child => {
    if (typeof child === 'string') {
      return highlightSearchTerms(child, searchTerm);
    }
    return child;
  });
};

// Custom ReactMarkdown components that apply search highlighting
const getHighlightedMarkdownComponents = (searchTerm) => {
  // Always disable strikethrough — AI sometimes generates ~~ around em dashes or financial ranges
  const baseComponents = {
    del: ({children}) => <span>{children}</span>,
  };

  if (!searchTerm?.trim()) return baseComponents;
  
  return {
    ...baseComponents,
    p: ({children, ...props}) => <p {...props}>{processTextChildren(children, searchTerm)}</p>,
    li: ({children, ...props}) => <li {...props}>{processTextChildren(children, searchTerm)}</li>,
    td: ({children, ...props}) => <td {...props}>{processTextChildren(children, searchTerm)}</td>,
    th: ({children, ...props}) => <th {...props}>{processTextChildren(children, searchTerm)}</th>,
    strong: ({children, ...props}) => <strong {...props}>{processTextChildren(children, searchTerm)}</strong>,
    em: ({children, ...props}) => <em {...props}>{processTextChildren(children, searchTerm)}</em>,
  };
};

  // Handle favorite toggle
  const handleFavoriteToggle = async (questionId, currentlyFavorited) => {
    const userId = user?.uid || user?.id;
    if (!userId) return;

    try {
      const questionRef = doc(db, 'questions', questionId);

      if (currentlyFavorited) {
        await updateDoc(questionRef, {
          favoritedBy: arrayRemove(userId)
        });
        toast.success('Removed from favorites');
      } else {
        await updateDoc(questionRef, {
          favoritedBy: arrayUnion(userId)
        });
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast.error('Failed to update favorite status');
    }
  };

  // Check if question is favorited by current user (stable reference)
const isQuestionFavorited = useCallback((question) => {
  const userId = user?.uid || user?.id;
  return !!(question.favoritedBy && question.favoritedBy.includes(userId));
}, [user?.uid, user?.id]);
  
  // Handle favorite toggle for Business Updates
const handleUpdateFavoriteToggle = async (updateId, currentlyFavorited) => {
    const userId = user?.uid || user?.id;
    if (!userId) return;
    try {
        const updateRef = doc(db, 'businessUpdates', updateId);
        if (currentlyFavorited) {
            await updateDoc(updateRef, {
                favoritedBy: arrayRemove(userId)
            });
            toast.success('Removed from favorites');
        } else {
            await updateDoc(updateRef, {
                favoritedBy: arrayUnion(userId)
            });
            toast.success('Added to favorites');
        }
    } catch (error) {
        console.error('Error updating update favorite status:', error);
        toast.error('Failed to update favorite status');
    }
};

// Check if a business update is favorited by current user
const isUpdateFavorited = (update) => {
    const userId = user?.uid || user?.id;
    return update.favoritedBy && update.favoritedBy.includes(userId);
};

  // Generic favorite checker for sorting
const isItemFavorited = (item) => {
    if (item.dataType === 'question') {
        return isQuestionFavorited(item);
    }
    if (item.dataType === 'update') {
        return isUpdateFavorited(item);
    }
    return false;
};

  // Handle feedback submission (Generic for any item type)
const handleFeedbackSubmit = async () => {
  if (!selectedItemForFeedback || feedbackRating === 0) return;
  const userId = user?.uid || user?.id;
  if (!userId) {
    toast.error('User not authenticated');
    return;
  }
  setSubmittingFeedback(true);
  try {
    const collectionName = selectedItemForFeedback.dataType === 'update' ? 'businessUpdates' : 'questions';
    const docId = selectedItemForFeedback.id;
    const itemRef = doc(db, collectionName, docId);
    
    const feedbackData = {
      rating: feedbackRating,
      comment: feedbackText.trim(),
      submittedAt: serverTimestamp(),
      submittedBy: user.email
    };

    await updateDoc(itemRef, {
      feedback: feedbackData,
      updatedAt: serverTimestamp()
    });

    toast.success('Feedback submitted successfully!');
    closeFeedbackDialog(); // This now correctly resets the generic state
    if (onQuestionUpdate) onQuestionUpdate();

  } catch (error) {
    console.error('Error submitting feedback:', error);
    toast.error('Failed to submit feedback');
  } finally {
    setSubmittingFeedback(false);
  }
};

  // Handle follow-up submission
  const handleFollowUpSubmit = async () => {
  const text = (followUpRef.current?.value || '').trim();
  if (!text || !selectedQuestionForFollowUp) return;

  const userId = user?.uid || user?.id;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setSubmittingFollowUp(true);
    const toastId = toast.loading("Submitting follow-up question...");
    let newFollowUpId = null;

    try {
      // Step 1: Create the follow-up question document in Firestore
      const followUpData = {
        questionText: text,
        parentId: selectedQuestionForFollowUp.id,
        clientId: selectedQuestionForFollowUp.clientId,
        organizationId: selectedQuestionForFollowUp.organizationId || null,
        userId: userId,
        fcfoId: userId, // For compatibility
        userEmail: user.email,
        type: 'follow-up',
        status: 'pending',
        aiStatus: 'processing',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'questions'), followUpData);
      newFollowUpId = docRef.id;

      toast.update(toastId, { render: "Follow-up saved. Triggering AI analysis...", type: "info" });

      // This is the "After" code with the timeout fix
      const functions = getFunctions(); // Get functions instance
      const pocAutomatedAnswer = httpsCallable(functions, 'pocAutomatedAnswer', {
  timeout: 120000
});

await pocAutomatedAnswer({
  userQuestion: text, // from the ref
  clientId: selectedQuestionForFollowUp.clientId,
  questionId: newFollowUpId
});

      toast.update(toastId, { render: "AI processing started! Your answer will appear shortly.", type: "success", isLoading: false, autoClose: 5000 });
      
      closeFollowUpDialog(); // This will reset state and close the dialog
      if (onQuestionUpdate) onQuestionUpdate();

    } catch (error) {
      console.error('Error submitting follow-up question:', error);
      toast.update(toastId, { render: `An error occurred: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });

      // If the document was created but the AI trigger failed, update its status to 'error'
      if (newFollowUpId) {
        const questionRef = doc(db, 'questions', newFollowUpId);
        await updateDoc(questionRef, { status: 'error', aiStatus: 'error' });
      }
    } finally {
      setSubmittingFollowUp(false);
    }
  };

// Add this new function after the existing handleFollowUpSubmit function
const handleBusinessUpdateFollowUpSubmit = async () => {
  const text = (buFollowUpRef.current?.value || '').trim();
  if (!text || !selectedUpdateForFollowUp) return;

  const userId = user?.uid || user?.id;
  if (!userId) {
    toast.error('User not authenticated');
    return;
  }

  setSubmittingBuFollowUp(true); // Set submitting state right before the async call
  const toastId = toast.loading("Submitting follow-up question on SavvaiSignals...");

  try {
    const functions = getFunctions();
    const handleBusinessUpdateFollowUpFunction = httpsCallable(functions, 'handleBusinessUpdateFollowUp', {
      timeout: 300000  // Set timeout when creating the callable reference
    });
    
    const result = await handleBusinessUpdateFollowUpFunction({
    businessUpdateId: selectedUpdateForFollowUp.id,
    followUpQuestion: text
  });

    if (result.data.success) {
      toast.update(toastId, {
        render: "Follow-up question submitted successfully! Answer will appear shortly.",
        type: "success",
        isLoading: false,
        autoClose: 5000
      });

      setBuFollowUpDialogOpen(false);
  if (buFollowUpRef.current) buFollowUpRef.current.value = '';
  setSelectedUpdateForFollowUp(null);

      if (onQuestionUpdate) onQuestionUpdate();
    } else {
      throw new Error(result.data.error || "Failed to submit follow-up question.");
    }
  } catch (error) {
    console.error('Error submitting SavvaiSignals follow-up:', error);
    toast.update(toastId, {
      render: `Error submitting follow-up: ${error.message}`,
      type: "error",
      isLoading: false,
      autoClose: 5000
    });
  } finally {
    setSubmittingBuFollowUp(false); // Ensure state is always reset
  }
};

  // Handle Asana button click
const handleOpenAsanaModal = useCallback((item) => {
  const client = clients.find(c => c.id === item.clientId);
  
  // Determine which connection context to use
  let context = null;
  if (client?.asanaConnection?.status === 'connected') {
    context = { type: 'client', id: client.id };
  } else if (userAsanaConnection?.status === 'connected') {
    context = { type: 'user' };
  }

  setSelectedItemForAsana({ ...item, asanaContext: context });
  setIsAsanaModalOpen(true);
}, [clients, userAsanaConnection]);

// Handle share button click for any item type
const handleShareItem = useCallback((item) => {
    setSelectedItemForShare(item);
    setShareModalOpen(true);
}, []);

  // Handle edit note button click
  const handleEditNote = useCallback((item) => {
  setSelectedItemForNote(item);
  setNoteText(item.userNotes || '');
  setNoteDialogOpen(true);
}, []);

  // Handle save note
  // Generic note saver for any item type
const handleSaveNote = useCallback(async () => {
  if (!selectedItemForNote) return;
  try {
    const collectionName = selectedItemForNote.dataType === 'update' ? 'businessUpdates' : 'questions';
    const docId = selectedItemForNote.id;
    const itemRef = doc(db, collectionName, docId);

    await updateDoc(itemRef, {
      userNotes: noteText.trim(),
      updatedAt: serverTimestamp()
    });

    toast.success('Note saved successfully!');
    setNoteDialogOpen(false);
    setSelectedItemForNote(null);
    setNoteText('');
  } catch (error) {
    console.error('Error saving note:', error);
    toast.error('Failed to save note');
  }
}, [noteText, selectedItemForNote]);

  // Generate CSV content (Updated with review feedback)
const generateCSVContent = (itemsToExport, fields) => {
    const fieldMap = {
        questionId: 'ID',
        clientName: 'Client Name',
        dateCreated: 'Date Created',
        questionText: 'Question Text',
        answerText: 'Answer Text',
        feedbackRating: 'Feedback Rating',
        feedbackText: 'Feedback Comments',
        userNotes: 'User Notes',
        bu_title: 'Update Title',
        bu_content: 'Update Content',
        bu_sources: 'Update Sources',
    };

    const headers = [];
    Object.keys(fieldMap).forEach(fieldKey => {
        if (fields[fieldKey]) {
            headers.push(fieldMap[fieldKey]);
        }
    });

    const rows = [headers.map(h => `"${h}"`).join(',')];

    itemsToExport.forEach(item => {
        const rowData = {};

        // CORRECTED: The check for fields.questionText is removed.
        if (item.dataType === 'question') {
            rowData['ID'] = fields.questionId ? item.id : '';
            rowData['Client Name'] = fields.clientName ? item.clientName : '';
            rowData['Date Created'] = fields.dateCreated ? (item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : '') : '';
            rowData['Question Text'] = fields.questionText ? item.questionText : '';
            rowData['Answer Text'] = fields.answerText ? item.answer : '';
            rowData['Feedback Rating'] = fields.feedbackRating ? item.feedback?.rating : '';
            rowData['Feedback Comments'] = fields.feedbackText ? item.feedback?.comment : '';
            rowData['User Notes'] = fields.userNotes ? item.userNotes : '';
        } else if (item.dataType === 'update') {
            rowData['ID'] = fields.questionId ? item.id : '';
            rowData['Client Name'] = fields.clientName ? item.clientName : '';
            rowData['Date Created'] = fields.dateCreated ? (item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : '') : '';
            rowData['Update Title'] = fields.bu_title ? item.title : '';
            rowData['Update Content'] = fields.bu_content ? item.content : '';
            rowData['Update Sources'] = fields.bu_sources ? (item.sources?.join('; ') || '') : '';
        }

        const row = headers.map(header => {
            const cell = rowData[header] || '';
            return `"${(cell).toString().replace(/"/g, '""')}"`;
        });
        rows.push(row.join(','));

        // CORRECTED: Now handles follow-ups for BOTH questions and business updates.
        if (fields.includeFollowUps && item.followUps?.length > 0) {
            item.followUps.forEach(followUp => {
                const followUpRowData = {
                    'ID': fields.questionId ? followUp.id : '',
                    'Client Name': fields.clientName ? item.clientName : '',
                    'Date Created': fields.dateCreated ? (followUp.createdAt?.toDate ? followUp.createdAt.toDate().toISOString() : '') : '',
                    'Question Text': fields.questionText ? `[Follow-up] ${followUp.questionText}` : '',
                    'Answer Text': fields.answerText ? followUp.answer : '',
                };
                const followUpRow = headers.map(header => {
                    const cell = followUpRowData[header] || '';
                    return `"${(cell).toString().replace(/"/g, '""')}"`;
                });
                rows.push(followUpRow.join(','));
            });
        }
    });

    return rows.join('\n');
};
// Generate Word document (Updated with full Markdown formatting support)
const generateWordDocument = async (itemsToExport, fields) => {

    // Helper to add a simple labeled field (plain text)
    const addField = (sectionChildren, label, value) => {
        if (value && value.toString().trim()) {
            sectionChildren.push(new Paragraph({
                children: [
                    new TextRun({ text: `${label}: `, bold: true }),
                    new TextRun({ text: value.toString() }),
                ],
                spacing: { after: 100 },
            }));
        }
    };

    // NEW: Parse Markdown content into formatted Word paragraphs
const parseMarkdownContent = (content) => {
    const elements = [];
    if (!content) return elements;

    const lines = content.split('\n');
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmedLine = line.trim();

        // Handle Heading 1
        if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('## ')) {
            elements.push(new Paragraph({
                text: trimmedLine.substring(2).trim(),
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 150 }
            }));
            i++;
        }
        // Handle Heading 2
        else if (trimmedLine.startsWith('## ') && !trimmedLine.startsWith('### ')) {
            elements.push(new Paragraph({
                text: trimmedLine.substring(3).trim(),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 250, after: 120 }
            }));
            i++;
        }
        // Handle Heading 3
        else if (trimmedLine.startsWith('### ')) {
            elements.push(new Paragraph({
                text: trimmedLine.substring(4).trim(),
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 }
            }));
            i++;
        }
        // Handle Horizontal Rule
        else if (trimmedLine === '---') {
            elements.push(new Paragraph({
                text: '________________________________',
                spacing: { before: 200, after: 200 }
            }));
            i++;
        }
        // Handle Bullet Lists (render as indented text with bullet symbol)
        else if (trimmedLine.startsWith('- ')) {
            const bulletText = trimmedLine.substring(2).trim();
            
            // Parse bold and italic within bullet text
            const textRuns = [new TextRun({ text: '• ' })]; // Bullet symbol
            const formatRegex = /(\*\*.*?\*\*|\*.*?\*)/g;
            const parts = bulletText.split(formatRegex).filter(p => p);
            
            parts.forEach(part => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                    textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                } else {
                    textRuns.push(new TextRun({ text: part }));
                }
            });

            elements.push(new Paragraph({
                children: textRuns,
                spacing: { after: 50 },
                indent: { left: 360 }
            }));
            i++;
        }
        // Handle nested bullets (two or more spaces before -)
        else if (/^\s{2,}-\s/.test(line)) {
            const leadingSpaces = line.search(/\S/);
            const indentLevel = Math.floor(leadingSpaces / 2);
            const bulletText = trimmedLine.substring(2).trim();
            
            const textRuns = [new TextRun({ text: '  ◦ ' })]; // Nested bullet symbol
            const formatRegex = /(\*\*.*?\*\*|\*.*?\*)/g;
            const parts = bulletText.split(formatRegex).filter(p => p);
            
            parts.forEach(part => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                    textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                } else {
                    textRuns.push(new TextRun({ text: part }));
                }
            });

            elements.push(new Paragraph({
                children: textRuns,
                spacing: { after: 50 },
                indent: { left: 360 + (indentLevel * 360) }
            }));
            i++;
        }
        // Handle Numbered Lists
        else if (/^\d+\.\s/.test(trimmedLine)) {
            const match = trimmedLine.match(/^(\d+)\.\s(.+)$/);
            if (match) {
                const number = match[1];
                const numberText = match[2];
                
                const textRuns = [new TextRun({ text: `${number}. ` })];
                const formatRegex = /(\*\*.*?\*\*|\*.*?\*)/g;
                const parts = numberText.split(formatRegex).filter(p => p);
                
                parts.forEach(part => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                        textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                    } else {
                        textRuns.push(new TextRun({ text: part }));
                    }
                });

                elements.push(new Paragraph({
                    children: textRuns,
                    spacing: { after: 50 },
                    indent: { left: 360 }
                }));
            }
            i++;
        }
        // Handle paragraphs with bold or italic formatting
        else if (trimmedLine && (trimmedLine.includes('**') || trimmedLine.includes('*'))) {
            const textRuns = [];
            const formatRegex = /(\*\*.*?\*\*|\*[^*]+?\*)/g;
            const parts = line.split(formatRegex).filter(p => p);
            
            if (parts.length === 0 && trimmedLine) {
                textRuns.push(new TextRun({ text: line }));
            } else {
                parts.forEach(part => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !part.includes('**')) {
                        textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                    } else {
                        textRuns.push(new TextRun({ text: part }));
                    }
                });
            }

            elements.push(new Paragraph({
                children: textRuns,
                spacing: { after: 100 }
            }));
            i++;
        }
        // Handle regular paragraphs
        else if (trimmedLine) {
            elements.push(new Paragraph({
                text: line,
                spacing: { after: 100 }
            }));
            i++;
        }
        // Skip empty lines
        else {
            i++;
        }
    }

    return elements;
};

    // NEW: Add a labeled content field with Markdown parsing
    const addMarkdownField = (sectionChildren, label, content) => {
  if (content && content.toString().trim()) {
    sectionChildren.push(new Paragraph({
      children: [new TextRun({ text: `${label}:`, bold: true })],
      spacing: { before: 100, after: 50 }
    }));
    const formattedContent = parseMarkdownContent(content.toString());
    sectionChildren.push(...formattedContent);
  }
};


    // Helper to render follow-ups with Markdown support
    const renderFollowUpsForWord = (sectionChildren, followUps) => {
        if (fields.includeFollowUps && followUps?.length > 0) {
            sectionChildren.push(new Paragraph({
                text: "Follow-up Thread:",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
            }));
            followUps.forEach((followUp, followUpIndex) => {
                // Follow-up question (plain text is fine)
                addField(sectionChildren, `  Follow-up ${followUpIndex + 1}`, followUp.questionText);
                // Follow-up answer (use Markdown parsing)
                if (followUp.answer) {
                    addMarkdownField(sectionChildren, "    Answer", followUp.answer);
                }
            });
        }
    };

    // GOVERNANCE: Get client info for governance header
    const currentClient = clients?.find(c => c.id === itemsToExport[0]?.clientId);
    
    // GOVERNANCE: Generate header paragraphs
    const governanceHeader = generateGovernanceHeader({
        threadId: itemsToExport.length === 1 ? itemsToExport[0].id : `Batch Export (${itemsToExport.length} items)`,
        clientName: currentClient?.officialCompanyName || currentClient?.name || itemsToExport[0]?.clientName || 'Multiple Clients',
        advisorName: user?.displayName || user?.name || user?.email?.split('@')[0] || 'FCFO',
        advisorEmail: user?.email,
        exportTimestamp: new Date(),
        contentType: itemsToExport.some(i => i.dataType === 'update') ? 
            (itemsToExport.some(i => i.dataType === 'question') ? 'Q&A + SavvaiSignals' : 'SavvaiSignals') : 
            'Q&A Export',
                quickBooksLastSync: currentClient?.quickbooksLastFetchedAt || null,
        aiProvider: itemsToExport[0]?.provider || 'Anthropic Claude',
    });

    const doc = new Document({
        sections: [{
            children: [
                ...governanceHeader,
                new Paragraph({
                    text: "Savvai.ai Export",
                    heading: HeadingLevel.TITLE,
                    spacing: { after: 200 },
                }),
                new Paragraph({
                    text: `Export Date: ${new Date().toLocaleDateString()}`,
                    spacing: { after: 400 },
                }),
                new Paragraph({
                    text: `Total Items: ${itemsToExport.length}`,
                    spacing: { after: 400 },
                }),
            ],
        }],
    });

    let questionCount = 0;
    let updateCount = 0;

    itemsToExport.forEach((item) => {
        const itemChildren = [];

        if (item.dataType === 'question') {
            questionCount++;
            itemChildren.push(new Paragraph({
                text: `Question ${questionCount}`,
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 100 },
            }));
            if (fields.questionId) addField(itemChildren, "Question ID", item.id);
            if (fields.clientName) addField(itemChildren, "Client", item.clientName);
            if (fields.dateCreated) addField(itemChildren, "Created", item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'N/A');
            if (fields.questionText) addField(itemChildren, "Question", item.questionText);
            
            // CHANGED: Use Markdown parsing for answers
            if (fields.answerText && item.answer) {
                addMarkdownField(itemChildren, "Answer", item.answer);
            }
            
            if (fields.userNotes) addField(itemChildren, "Your Notes", item.userNotes);
            if (fields.feedbackRating) addField(itemChildren, "Rating", item.feedback?.rating ? `${item.feedback.rating}/5 stars` : null);
            if (fields.feedbackText) addField(itemChildren, "Feedback", item.feedback?.comment);
            
            renderFollowUpsForWord(itemChildren, item.followUps);

        } else if (item.dataType === 'update') {
            updateCount++;
            itemChildren.push(new Paragraph({
                text: `Business Update ${updateCount}`,
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 300, after: 100 },
            }));
            if (fields.questionId) addField(itemChildren, "Update ID", item.id);
            if (fields.clientName) addField(itemChildren, "Client", item.clientName);
            if (fields.dateCreated) addField(itemChildren, "Created", item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'N/A');
            if (fields.bu_title) addField(itemChildren, "Title", item.title);
            
            // Use Markdown parser for Business Update content
            if (fields.bu_content && item.content) {
                addMarkdownField(itemChildren, "Content", item.content);
            }
            
            if (fields.bu_sources) addField(itemChildren, "Sources", item.sources?.join('\n'));

            renderFollowUpsForWord(itemChildren, item.followUps);
        }
        
        doc.addSection({ children: itemChildren });
    });

    // GOVERNANCE: Add footer as final section
    const governanceFooter = generateGovernanceFooter();
    doc.addSection({ children: governanceFooter });

    return await Packer.toBlob(doc);
};


  // Handle export (updated to support both CSV and Word)
  const handleExport = async () => {
    if (!processedItems.length) {
      toast.error('No questions to export');
      return;
    }

    try {
      setIsExporting(true);
      const filenameBase = `sds_questions_export_${new Date().toISOString().split('T')[0]}`;
      let blob;
      let filename;

      if (exportFormat === 'csv') {
        const csvContent = generateCSVContent(processedItems, exportFields);
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        filename = `${filenameBase}.csv`;
      } else if (exportFormat === 'word') {
        blob = await generateWordDocument(processedItems, exportFields);
        filename = `${filenameBase}.docx`;
    }

      if (blob) {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(`${exportFormat.toUpperCase()} exported successfully!`);
      }

      setExportModalOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle single-item export to Word
  const handleSingleItemExport = async (item) => {
    try {
      const filename = `savvai_export_${item.dataType === 'question' ? 'qa' : 'signal'}_${new Date().toISOString().split('T')[0]}.docx`;
      const blob = await generateWordDocument([item], exportFields);
      
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully!');
    } catch (error) {
      console.error('Single item export failed:', error);
      toast.error('Export failed. Please try again.');
    }
  };

  // Call the correct Cloud Function based on content
const handleShare = useCallback(async (shareData) => {
    try {
        const functions = getFunctions();
        let callableFunction;

        // Detect type based on payload structure
        if (shareData.questionThread) {
            callableFunction = httpsCallable(functions, 'shareQuestionThreadByEmail');
        } else if (shareData.updateData) {
            callableFunction = httpsCallable(functions, 'shareBusinessUpdateByEmail');
        } else {
            throw new Error("Invalid data structure for sharing.");
        }

        const result = await callableFunction(shareData);
                
        if (result.data.success) {
            console.log('Content shared successfully:', result.data);
            return result.data;
        } else {
            throw new Error(result.data.error || 'Failed to share content');
        }
    } catch (error) {
        console.error('Error calling share function:', error);
        throw error;
    }
}, []);

  // Open follow-up dialog
  const openFollowUpDialog = (question) => {
    setSelectedQuestionForFollowUp(question);
    setFollowUpDialogOpen(true);
  };

  // Close follow-up dialog
  const closeFollowUpDialog = () => {
    setFollowUpDialogOpen(false);
  if (followUpRef.current) followUpRef.current.value = '';
  setSelectedQuestionForFollowUp(null);
  };

  // Open feedback dialog (Generic for any item type)
const openFeedbackDialog = (item) => {
  setSelectedItemForFeedback(item);
  // Pre-populate with existing feedback if available
  if (item.feedback) {
    setFeedbackRating(item.feedback.rating || 0);
    setFeedbackText(item.feedback.comment || '');
  } else {
    setFeedbackRating(0);
    setFeedbackText('');
  }
  setFeedbackDialogOpen(true);
};

  // Close feedback dialog
const closeFeedbackDialog = () => {
  setFeedbackDialogOpen(false);
  setFeedbackRating(0);
  setFeedbackText('');
  setSelectedItemForFeedback(null);
};

  // NEW: Handle Generate Decision Memo
  const handleGenerateDecisionMemo = async (parentId, signalNumber, contextText, parentType) => {
    const toastId = toast.loading('Generating Decision Memo... (this may take 2-3 minutes)');
    
    try {
      const functions = getFunctions();
      const generateMemoFn = httpsCallable(functions, 'generateDecisionMemo', {
        timeout: 600000  // 10 minutes in milliseconds
      });
      
      const result = await generateMemoFn({
        clientId,
        parentId,
        signalNumber,
        contextText,
        parentType
      });
      
      if (result.data.success) {
        toast.update(toastId, {
          render: 'Decision Memo generated successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
        
        // Scroll to the newly created Decision Memo
        setTimeout(() => {
          const newMemoElement = document.getElementById(result.data.memoId);
          if (newMemoElement) {
            newMemoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      } else {
        throw new Error(result.data.error || 'Failed to generate Decision Memo');
      }
    } catch (error) {
      console.error('Error generating Decision Memo:', error);
      toast.update(toastId, {
        render: error.message || 'Failed to generate Decision Memo',
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    }
  };

  // Clear date filters
  const clearDateFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  // Fetch questions based on clientId
  // ========================================================================
// STABILIZED fetchFeedItems (Fixes Infinite Loop)
// ========================================================================

// 1. Extract primitive ID for stability
const userId = user?.uid || user?.id;

// ============================================================================
// HYBRID PAGINATION: Fast initial load + background full history
// ============================================================================
const INITIAL_BATCH_SIZE = 50;
const LOADING_NOTE_DELAY_MS = 3000;

const fetchFeedItems = useCallback(() => {
  // If no userId, just clear and exit
  if (!userId) {
    setItems([]);
    setLoading(false);
    return () => {};
  }

  // Calculate 90-day boundary for default view
const getDefaultDateBoundary = () => {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_DAYS_BACK);
  return date;
}; 
 
  setLoading(true);
  setIsLoadingFullHistory(false);
  setShowLoadingNote(false);
  setError(null);

  // Timer reference for cleanup
  let loadingNoteTimer = null;

  try {
    const questionsRef = collection(db, 'questions');
    const updatesRef = collection(db, 'businessUpdates');

    // Build base queries - apply 90-day filter unless showAllHistory is true
const dateBoundary = showAllHistory ? null : getDefaultDateBoundary();

const baseQQuery = clientId === 'ALL'
  ? (dateBoundary 
      ? query(questionsRef, where('fcfoId', '==', userId), where('createdAt', '>=', dateBoundary), orderBy('createdAt', 'desc'))
      : query(questionsRef, where('fcfoId', '==', userId), orderBy('createdAt', 'desc')))
  : (dateBoundary
      ? query(questionsRef, where('clientId', '==', clientId), where('fcfoId', '==', userId), where('createdAt', '>=', dateBoundary), orderBy('createdAt', 'desc'))
      : query(questionsRef, where('clientId', '==', clientId), where('fcfoId', '==', userId), orderBy('createdAt', 'desc')));

const baseUQuery = clientId === 'ALL'
  ? (dateBoundary
      ? query(updatesRef, where('fcfoId', '==', userId), where('createdAt', '>=', dateBoundary), orderBy('createdAt', 'desc'))
      : query(updatesRef, where('fcfoId', '==', userId), orderBy('createdAt', 'desc')))
  : (dateBoundary
      ? query(updatesRef, where('clientId', '==', clientId), where('fcfoId', '==', userId), where('createdAt', '>=', dateBoundary), orderBy('createdAt', 'desc'))
      : query(updatesRef, where('clientId', '==', clientId), where('fcfoId', '==', userId), orderBy('createdAt', 'desc')));

    // Build limited queries for fast initial load
    const initialQQuery = query(baseQQuery, limit(INITIAL_BATCH_SIZE));
    const initialUQuery = query(baseUQuery, limit(INITIAL_BATCH_SIZE));

    // Track if initial load has completed
    let initialLoadComplete = false;
    let fullLoadComplete = false;

    // STEP 1: Fast initial load with limit
    Promise.all([
      getDocs(initialQQuery),
      getDocs(initialUQuery)
    ]).then(([questionsSnap, updatesSnap]) => {
      const initialQuestions = questionsSnap.docs.map(d => ({ ...d.data(), id: d.id, dataType: 'question' }));
      const initialUpdates = updatesSnap.docs.map(d => ({ ...d.data(), id: d.id, dataType: 'update' }));
      
      const combined = [...initialQuestions, ...initialUpdates].sort(
        (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
      );
      
      // Only set if onSnapshot hasn't already delivered full data
      if (!initialLoadComplete && !fullLoadComplete) {
        console.log(`[PERF] Initial load complete: ${combined.length} items (${initialQuestions.length} questions, ${initialUpdates.length} updates)`);
        setItems(combined);
        setLoading(false);
        
        // If we got less than the limit, we have all the data already
        const hasMoreData = questionsSnap.size >= INITIAL_BATCH_SIZE || updatesSnap.size >= INITIAL_BATCH_SIZE;
        if (hasMoreData) {
          setIsLoadingFullHistory(true);
          // Show loading note only if background load takes > 3 seconds
          loadingNoteTimer = setTimeout(() => {
            if (!fullLoadComplete) {
              setShowLoadingNote(true);
            }
          }, LOADING_NOTE_DELAY_MS);
        }
        initialLoadComplete = true;
      }
    }).catch(err => {
      console.error('[ERROR] Initial load failed:', err);
      // Don't set error - onSnapshot will still try to load
    });

    // STEP 2: Full history via onSnapshot (runs in parallel, delivers when ready)
    let latestQuestions = null;
    let latestUpdates = null;

    const maybeCommit = () => {
      if (latestQuestions === null || latestUpdates === null) return;
      
      fullLoadComplete = true; // Prevent initial load from overwriting & stop loading note timer
      
      // Clear the timer if it hasn't fired yet
      if (loadingNoteTimer) {
        clearTimeout(loadingNoteTimer);
        loadingNoteTimer = null;
      }
      
      const combined = [...latestQuestions, ...latestUpdates].sort(
        (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
      );
      
      console.log(`[PERF] Full history loaded: ${combined.length} items (${latestQuestions.length} questions, ${latestUpdates.length} updates)`);
      setItems(combined);
      setLoading(false);
      setIsLoadingFullHistory(false);
      setShowLoadingNote(false);

      // Auto-expand recent items logic
      const now = new Date();
      const newIds = combined
        .filter(it => {
          const d = it.createdAt?.toDate?.();
          return d && (now - d) / 1000 < 15;
        })
        .map(it => it.id);
      if (newIds.length) {
        setExpandedQuestions(prev => new Set([...prev, ...newIds]));
      }
    };

    const unsubQ = onSnapshot(
      baseQQuery,
      snap => {
        latestQuestions = snap.docs.map(d => ({ ...d.data(), id: d.id, dataType: 'question' }));
        maybeCommit();
      },
      err => {
        console.error('[ERROR] questions snapshot:', err);
        setError(`Failed to load questions: ${err.message}`);
        setLoading(false);
        setIsLoadingFullHistory(false);
        setShowLoadingNote(false);
      }
    );

    const unsubU = onSnapshot(
      baseUQuery,
      snap => {
        latestUpdates = snap.docs.map(d => ({ ...d.data(), id: d.id, dataType: 'update' }));
        maybeCommit();
      },
      err => {
        console.error('[ERROR] updates snapshot:', err);
        setError(`Failed to load business updates: ${err.message}`);
        setLoading(false);
        setIsLoadingFullHistory(false);
        setShowLoadingNote(false);
      }
    );

    return () => {
      // Cleanup timer on unmount
      if (loadingNoteTimer) {
        clearTimeout(loadingNoteTimer);
      }
      unsubQ();
      unsubU();
    };
  } catch (err) {
    console.error('[ERROR] setting up listeners:', err);
    setError(`Failed to load feed: ${err.message}`);
    setLoading(false);
    setIsLoadingFullHistory(false);
    setShowLoadingNote(false);
    return () => {};
  }
}, [clientId, userId, showAllHistory]);

// 3. The useEffect remains the same, but now fetchFeedItems is stable!
useEffect(() => {
  const unsubscribe = fetchFeedItems();
  return () => {
    if (unsubscribe) unsubscribe();
  };
}, [fetchFeedItems]);

  // This effect listens for a search query passed down from a parent component (e.g., AMS modal)
useEffect(() => {
  if (forwardedSearchQuery?.id) {
    const newSearchTerm = forwardedSearchQuery.id;
    setLocalSearchQuery(newSearchTerm);
  }
}, [forwardedSearchQuery]);

// Reset to 90-day default view when client changes
useEffect(() => {
  setShowAllHistory(false);
}, [clientId]);

  // Process questions for display (search, sort, client name mapping, threading, date filtering)
  const processedItems = useMemo(() => {
    let filtered = [...items];

    // Apply Content Type Filter
    if (typeFilter === 'qa_only') {
      filtered = filtered.filter(item => item.dataType === 'question');
    } else if (typeFilter === 'updates_only') {
      filtered = filtered.filter(item => item.dataType === 'update');
    }

    // Build follow-up maps from questions collection (for old-style follow-ups)
    const followUps = filtered.filter(item => item.dataType === 'question' && item.parentId);
    const questionFollowUpMap = {};
    const businessUpdateFollowUpMap = {};

    followUps.forEach(followUp => {
      if (followUp.parentType === 'business_update') {
        if (!businessUpdateFollowUpMap[followUp.parentId]) {
          businessUpdateFollowUpMap[followUp.parentId] = [];
        }
        businessUpdateFollowUpMap[followUp.parentId].push(followUp);
      } else {
        if (!questionFollowUpMap[followUp.parentId]) {
          questionFollowUpMap[followUp.parentId] = [];
        }
        questionFollowUpMap[followUp.parentId].push(followUp);
      }
    });

    // Re-assemble items, MERGING embedded followUps with mapped followUps
    let itemsWithFollowUps = filtered.map(item => {
  if (item.dataType === 'question' && !item.parentId) {
    // Questions: MERGE embedded followUps with mapped follow-ups
    const embeddedFollowUps = item.followUps || [];
    const mappedFollowUps = questionFollowUpMap[item.id] || [];
    const allFollowUps = [...embeddedFollowUps, ...mappedFollowUps];
    
    // Sort by creation date
    allFollowUps.sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
      return aTime - bTime;
    });
    
    return { ...item, followUps: allFollowUps };
  } else if (item.dataType === 'update') {
        // Business Updates: MERGE embedded followUps with mapped follow-ups
        const embeddedFollowUps = item.followUps || [];
        const mappedFollowUps = businessUpdateFollowUpMap[item.id] || [];
        const allFollowUps = [...embeddedFollowUps, ...mappedFollowUps];
        
        // Sort by creation date
        allFollowUps.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
          return aTime - bTime;
        });
        
        return { ...item, followUps: allFollowUps };
      }
      return item;
    }).filter(item => !(item.dataType === 'question' && item.parentId));

    // Add client names
    if (clients) {
      itemsWithFollowUps.forEach(item => {
        const client = clients.find(c => c.id === item.clientId);
        item.clientName = client?.displayName || client?.name || 'Unknown Client';
        if (item.followUps) {
          item.followUps.forEach(followUp => {
            followUp.clientName = item.clientName;
          });
        }
      });
    }

    // Apply date range filter
    if (dateFrom || dateTo) {
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
      itemsWithFollowUps = itemsWithFollowUps.filter(item => {
        if (!item.createdAt?.toDate) return false;
        const itemDate = item.createdAt.toDate();
        if (fromDate && itemDate < fromDate) return false;
        if (toDate && itemDate > toDate) return false;
        return true;
      });
    }

    // Apply search filter
    if (localSearchQuery.trim()) {
      const searchTerm = localSearchQuery.toLowerCase();
      itemsWithFollowUps = itemsWithFollowUps.filter(item => {
        if (item.dataType === 'question') {
          const mainMatches =
            item.id?.toLowerCase().includes(searchTerm) ||
            item.questionText?.toLowerCase().includes(searchTerm) ||
            item.answer?.toLowerCase().includes(searchTerm) ||
            item.clientName?.toLowerCase().includes(searchTerm) ||
            item.feedback?.comment?.toLowerCase().includes(searchTerm) ||
            item.userNotes?.toLowerCase().includes(searchTerm);

          const followUpMatches = (item.followUps || []).some(followUp =>
            followUp.questionText?.toLowerCase().includes(searchTerm) ||
            followUp.answer?.toLowerCase().includes(searchTerm)
          );
          return mainMatches || followUpMatches;
        }
        if (item.dataType === 'update') {
          const mainMatches =
            item.id?.toLowerCase().includes(searchTerm) ||
            item.title?.toLowerCase().includes(searchTerm) ||
            item.content?.toLowerCase().includes(searchTerm) ||
            item.clientName?.toLowerCase().includes(searchTerm);

          const followUpMatches = (item.followUps || []).some(followUp =>
            (followUp.questionText && followUp.questionText.toLowerCase().includes(searchTerm)) ||
            (followUp.answer && followUp.answer.toLowerCase().includes(searchTerm))
          );
          return mainMatches || followUpMatches;
        }
        return false;
      });
    }

    // Apply sorting
    switch (localSortOption) {
      case 'oldest':
        itemsWithFollowUps.sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
        break;
      case 'favorites':
        itemsWithFollowUps.sort((a, b) => {
          const aFavorited = isItemFavorited(a);
          const bFavorited = isItemFavorited(b);
          if (aFavorited && !bFavorited) return -1;
          if (!aFavorited && bFavorited) return 1;
          return (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0);
        });
        break;
      case 'newest':
      default:
        itemsWithFollowUps.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
        break;
    }

    return itemsWithFollowUps;
  }, [items, localSearchQuery, localSortOption, typeFilter, clientId, clients, dateFrom, dateTo, isQuestionFavorited]);

  // Toggle question expansion
  const toggleExpansion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const toggleSourcesExpansion = (itemId) => {
    setExpandedSources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Enhanced timestamp formatting
  const formatTimestamp = (timestamp) => {
    if (!timestamp?.toDate) return 'Unknown date';

    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Recent timestamps
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    // Older timestamps
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Enhanced status display with icons and colors
  const getStatusDisplay = (status, aiStatus) => {
    if (status === 'answered') {
      return {
        label: 'Answered',
        color: 'success',
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />
      };
    }

    if (status === 'pending') {
      if (aiStatus === 'processing') {
        return {
          label: 'Processing',
          color: 'info',
          icon: <CircularProgress size={14} />
        };
      }
      return {
        label: 'Pending',
        color: 'warning',
        icon: <PendingIcon sx={{ fontSize: 16 }} />
      };
    }

    if (status === 'error' || aiStatus === 'error') {
      return {
        label: 'Error',
        color: 'error',
        icon: <ErrorIcon sx={{ fontSize: 16 }} />
      };
    }

    return {
      label: status || 'Unknown',
      color: 'default',
      icon: <ScheduleIcon sx={{ fontSize: 16 }} />
    };
  };

  // Render follow-up questions
 // Render follow-up questions in a chat style
  const renderFollowUps = (followUps, parentType, parentId) => {
    if (!followUps || followUps.length === 0) return null;

    return (
      <Box sx={{ ml: 1, mt: 2, borderLeft: '3px solid #f0f0f0', pl: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'primary.main', fontSize: '1.1rem', mb: 2 }}>
          <ChatIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
          Follow-up Thread
        </Typography>
        <Stack spacing={2.5}>
          {followUps.map((followUp) => {
            // NEW: Check if this is a BEP Decision Memo
            if (followUp.type === 'decision_memo' && followUp.framework === 'business_evolution_pyramid') {
              // Only count embedded followUps (not mapped Q&As from separate docs) to match Firestore array
              const embeddedOnly = followUps.filter(f => !f.parentId);
              const followUpIndex = embeddedOnly.findIndex(f => f.id === followUp.id);
              return (
                <Box key={followUp.id} sx={{ mt: 3, mb: 3 }}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #005DAA' }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#005DAA', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArticleIcon fontSize="small" />
                      Decision Memo (Business Evolution Pyramid)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Generated: {formatTimestamp(followUp.createdAt)}
                    </Typography>
                    {/* P2: Review Panel for BEP Decision Memo */}
                    <Box sx={{ mt: 1 }}>
                      <ArtifactReviewPanel
                        artifactType={parentType}
                        artifactId={parentId}
                        currentStatus={followUp.reviewStatus}
                        reviewedByEmail={followUp.reviewedByEmail}
                        reviewedAt={followUp.reviewedAt}
                        reviewNotes={followUp.reviewNotes}
                        reviewHistory={followUp.reviewHistory}
                        followUpIndex={followUpIndex}
                        compact={true}
                      />
                    </Box>
                  </Paper>
                  <BEPMemoDisplay followUpData={followUp} searchQuery={localSearchQuery} />
                </Box>
              );
            }
            
            // NEW: Check if this is a standard Decision Memo
if (followUp.type === 'decision_memo') {
    // Only count embedded followUps (not mapped Q&As from separate docs) to match Firestore array
    const embeddedOnly = followUps.filter(f => !f.parentId);
    const followUpIndex = embeddedOnly.findIndex(f => f.id === followUp.id);
  return (
    <Box key={followUp.id} sx={{ mt: 3, mb: 3 }}>
      <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon fontSize="small" />
          Decision Memo
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Generated: {formatTimestamp(followUp.createdAt)}
        </Typography>
        {/* P2: Review Panel for Standard Decision Memo */}
        <Box sx={{ mt: 1 }}>
          <ArtifactReviewPanel
            artifactType={parentType}
            artifactId={parentId}
            currentStatus={followUp.reviewStatus}
            reviewedByEmail={followUp.reviewedByEmail}
            reviewedAt={followUp.reviewedAt}
            reviewNotes={followUp.reviewNotes}
            reviewHistory={followUp.reviewHistory}
            followUpIndex={followUpIndex}
            compact={true}
          />
        </Box>
      </Paper>
      <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
        <Box sx={{ 
          fontSize: '1rem', 
          lineHeight: 1.7,
          '& h1, & h2, & h3': { mt: 2, mb: 1, fontWeight: 600 },
          '& p': { mb: 1.5 },
          '& ul, & ol': { pl: 3, mb: 2 },
          '& li': { mb: 0.5 },
          '& strong': { fontWeight: 600 },
          '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
          '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
          '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 }
        }}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={getHighlightedMarkdownComponents(localSearchQuery)}
          >
            {followUp.response || followUp.answer}
          </ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  );
}

            
            // EXISTING: Regular Q&A follow-up rendering
            return (
              <Box key={followUp.id}>
                {/* User's Follow-up Question */}
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#f5f5f5', maxWidth: '80%', borderRadius: '16px 16px 0 16px' }}>
                    <Typography variant="body2" sx={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                      {localSearchQuery ?
  highlightSearchTerms(followUp.questionText, localSearchQuery) :
  followUp.questionText
}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.8rem' }}>
                      {formatTimestamp(followUp.createdAt)}
                    </Typography>
                  </Paper>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PersonOutlineIcon />
                  </Avatar>
                </Stack>

                {/* AI's Answer to the Follow-up */}
                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <SmartToyIcon />
                  </Avatar>
                  <Paper elevation={2} sx={{ p: 1.5, bgcolor: 'white', maxWidth: '80%', borderRadius: '0 16px 16px 16px', border: '1px solid #e0e0e0' }}>
                      {followUp.answer && followUp.answer.trim() ? (
                    <Box sx={{ 
                      fontSize: '1rem', 
                      lineHeight: 1.7,
                      '& p': { margin: 0, marginBottom: '0.5em' },
                      '& p:last-child': { marginBottom: 0 },
                      '& ul, & ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                      '& li': { marginBottom: '0.25em' },
                      '& strong': { fontWeight: 600 },
                      '& table': { width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' },
                      '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                      '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 },
                      '& code': { backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '3px', fontSize: '0.9em', fontFamily: 'monospace' }
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {followUp.answer}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        Processing answer...
                      </Typography>
                    </Box>
                  )}
                  </Paper>
                </Stack>
                {/* P2: Review Panel for Follow-up Q&A */}
                {followUp.answer && followUp.answer.trim() && followUp.id && (
                  <Box sx={{ mt: 1, ml: 6 }}>
                    <ArtifactReviewPanel
                      artifactType="question"
                      artifactId={followUp.parentId ? followUp.id : parentId}
                      currentStatus={followUp.reviewStatus}
                      reviewedByEmail={followUp.reviewedByEmail}
                      reviewedAt={followUp.reviewedAt}
                      reviewNotes={followUp.reviewNotes}
                      reviewHistory={followUp.reviewHistory}
                      followUpIndex={followUp.parentId ? undefined : (() => {
                        const embeddedOnly = followUps.filter(f => !f.parentId);
                        return embeddedOnly.findIndex(f => f.id === followUp.id);
                      })()}
                      compact={true}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

// Render individual business update card
    const renderBusinessUpdateCard = (update) => {
    const canLeaveFeedback = true; // Always allow feedback on business updates
    const hasFeedback = update.feedback && update.feedback.rating;
    const isExpanded = expandedQuestions.has(update.id);
    const hasFollowUps = update.followUps && update.followUps.length > 0;
    return (
      <Card
        key={update.id}
        elevation={3}
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: 'secondary.light',
          backgroundColor: '#f3e5f5'
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  icon={<ArticleIcon />}
                  label="SavvaiSignals"
                  size="small"
                  color="secondary"
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
                {clientId === 'ALL' && (
                  <Chip
                    label={`Client: ${update.clientName || 'Unknown Client'}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontSize: '1.2rem', fontWeight: 600, cursor: 'pointer', mb: 1, color: 'secondary.dark' }}
                onClick={() => toggleExpansion(update.id)}
              >
                {localSearchQuery ? highlightSearchTerms(update.title || 'SavvaiSignals', localSearchQuery) : (update.title || 'SavvaiSignals')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 14 }} />
                {formatTimestamp(update.createdAt)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              {/* Favorite Toggle for Business Updates */}
              <Tooltip title={isUpdateFavorited(update) ? 'Remove from favorites' : 'Add to favorites'}>
                <IconButton
                  onClick={() => handleUpdateFavoriteToggle(update.id, isUpdateFavorited(update))}
                  size="small"
                  sx={{
                    color: isUpdateFavorited(update) ? 'warning.main' : 'text.secondary',
                    '&:hover': {
                      color: 'warning.main',
                      backgroundColor: 'rgba(255, 152, 0, 0.08)'
                    }
                  }}
                  aria-label={isUpdateFavorited(update) ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isUpdateFavorited(update) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {/* MOVED: Share Button */}
              <Tooltip title="Share via Email">
                <IconButton
                  onClick={() => handleShareItem(update)}
                  size="small"
                  disabled={clientIsActive === false}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* MOVED: Asana Button */}
              <Tooltip title={isAsanaAvailableForItem(update) ? "Create Task in Asana" : "Connect an Asana account to enable"}>
                <span> {/* Span is needed for tooltip on disabled button */}
                  <IconButton
                    size="small"
                    disabled={!isAsanaAvailableForItem(update)}
                    onClick={() => handleOpenAsanaModal(update)}
                  >
                    <PlaylistAddCheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {/* Export Single Item Button */}
              <Tooltip title="Export to Word">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSingleItemExport(update);
                  }}
                  size="small"
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton onClick={() => toggleExpansion(update.id)} size="small" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Collapse in={isExpanded} timeout="auto">
  <Divider sx={{ mb: 2 }} />
  <Box mb={2}>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>Update Details:</Typography>
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        padding: 3,
        borderRadius: 1,
        fontFamily: 'inherit',
        fontSize: '0.95rem',
        lineHeight: 1.7,
        color: '#333',
        // Header styling
        '& h1': { 
          fontSize: '1.75rem', 
          fontWeight: 700, 
          mt: 3, 
          mb: 2,
          color: '#1a1a1a',
          borderBottom: '2px solid #e0e0e0',
          pb: 1
        },
        '& h2': { 
          fontSize: '1.4rem', 
          fontWeight: 600, 
          mt: 3, 
          mb: 1.5,
          color: '#2a2a2a'
        },
        '& h3': { 
          fontSize: '1.15rem', 
          fontWeight: 600, 
          mt: 2, 
          mb: 1,
          color: '#333'
        },
        // Horizontal rule
        '& hr': { 
          my: 3, 
          border: 'none', 
          borderTop: '2px solid #e0e0e0' 
        },
        // Paragraph spacing
        '& p': { 
          mb: 1.5 
        },
        // Bold text
        '& strong': { 
          fontWeight: 600,
          color: '#1a1a1a'
        },
        // Lists
        '& ul, & ol': { 
          pl: 3, 
          mb: 2 
        },
        '& li': { 
          mb: 0.5 
        },
        // Tables (for priority matrix)
        '& table': { 
          width: '100%', 
          borderCollapse: 'collapse',
          mb: 3,
          mt: 2
        },
        '& th': { 
          backgroundColor: '#f0f0f0',
          padding: '12px',
          textAlign: 'left',
          fontWeight: 600,
          borderBottom: '2px solid #ddd'
        },
        '& td': { 
          padding: '10px 12px',
          borderBottom: '1px solid #e0e0e0'
        },
        '& tr:hover': {
          backgroundColor: '#f9f9f9'
        },
        // Code blocks (if you ever use them)
        '& code': {
          backgroundColor: '#f0f0f0',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '0.9em',
          fontFamily: 'monospace'
        }
      }}
    >
      <ReactMarkdown 
  remarkPlugins={[remarkGfm]}
  components={getHighlightedMarkdownComponents(localSearchQuery)}
>
  {update.content || 'No content available.'}
</ReactMarkdown>
    </Box>
  </Box>
            {/* P2: Review Status Panel for SavvaiSignals */}
            <Box mb={2}>
              <ArtifactReviewPanel
                artifactType="businessUpdate"
                artifactId={update.id}
                currentStatus={update.reviewStatus}
                reviewedByEmail={update.reviewedByEmail}
                reviewedAt={update.reviewedAt}
                reviewNotes={update.reviewNotes}
                reviewHistory={update.reviewHistory}
              />
            </Box>
            {/* User Notes Section for Business Updates */}
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>
                Your Notes:
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: 'white',
                  borderColor: 'grey.200'
                }}
              >
                {update.userNotes ? (
                  <Typography variant="body1" sx={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                    {localSearchQuery ?
  highlightSearchTerms(update.userNotes, localSearchQuery) :
  update.userNotes
}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No notes added yet.
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditNote(update)}
                  sx={{ mt: 1 }}
                >
                  {update.userNotes ? 'Edit Note' : 'Add Note'}
                </Button>
              </Paper>
            </Box>
            {update.sources && update.sources.length > 0 && (
              <Box mb={2}>
                <Box
                  onClick={() => toggleSourcesExpansion(update.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    mb: 1,
                    '&:hover': {
                      opacity: 0.7
                    }
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '1.1rem', mr: 1 }}>
                    Sources:
                  </Typography>
                  <ExpandMoreIcon
                    sx={{
                      transform: expandedSources.has(update.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  />
                </Box>
                <Collapse in={expandedSources.has(update.id)} timeout="auto">
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: 'white',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Stack spacing={1}>
                      {update.sources.map((source, index) => (
                        <Typography key={index} variant="body2" sx={{ fontSize: '0.9rem' }}>
                          <a href={source} target="_blank" rel="noopener noreferrer">
                            {source}
                          </a>
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Collapse>
              </Box>
            )}
            {hasFollowUps && renderFollowUps(update.followUps, 'businessUpdate', update.id)}
            
            {/* NEW: Generate Decision Memo buttons for each signal */}
            {(() => {
              const signalMatches = update.content.match(/### Signal \d+:[^\n]+/g);
              if (!signalMatches || signalMatches.length === 0) return null;

              return (
                <Box sx={{ mb: 2, mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1rem' }}>
                    Generate Decision Memo:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {signalMatches.map((signalTitle, index) => {
                      const signalNumber = index + 1;
                      const cleanTitle = signalTitle.replace(/### Signal \d+:\s*/, '').trim();
                      
                      return (
                        <Button
                          key={`signal-dm-${signalNumber}`}
                          variant="outlined"
                          size="small"
                          onClick={() => handleGenerateDecisionMemo(update.id, signalNumber, cleanTitle, 'business_update')}
                          sx={{ textTransform: 'none' }}
                        >
                          Signal {signalNumber}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })()}
            
            <Box display="flex" gap={1} mt={2} flexWrap="wrap">
              <Button variant="outlined" size="small" startIcon={<ReplyIcon />} onClick={() => { setSelectedUpdateForFollowUp(update); setBuFollowUpDialogOpen(true); }}>
                Ask Follow-Up on this Update
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FeedbackIcon />}
                onClick={() => openFeedbackDialog(update)}
                color={hasFeedback ? 'secondary' : 'primary'}
                disabled={!canLeaveFeedback || clientIsActive === false}
              >
                {hasFeedback ? 'Update Feedback' : 'Leave Feedback'}
              </Button>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Render individual question card
  const renderQuestionCard = (question) => {
    const isExpanded = expandedQuestions.has(question.id);
    const hasAttachment = question.fileUrl || question.attachmentURL;
    const isFavorited = isQuestionFavorited(question);
    const hasFollowUps = question.followUps && question.followUps.length > 0;
    const canAskFollowUp = question.status === 'answered' && clientIsActive;
    const canLeaveFeedback = question.status === 'answered';
    const hasFeedback = question.feedback && question.feedback.rating;
    const statusDisplay = getStatusDisplay(question.status, question.aiStatus);

    return (
      <Card
        key={question.id}
        elevation={3}
        sx={{
          mb: 2,
          transition: 'all 0.3s ease-in-out',
          border: isFavorited ? '2px solid' : '1px solid transparent',
          borderColor: isFavorited ? 'warning.main' : 'transparent',
          '&:hover': {
            elevation: 6,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          {/* Header Section */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              {/* Client tag for "All Clients" view */}
              {clientId === 'ALL' && (
                <Chip
                  label={`Client: ${question.clientName}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1, fontSize: '0.9rem' }}
                />
              )}

              {/* Status and indicators row */}
              <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                {isFavorited && (
                  <Chip
                    icon={<StarIcon />}
                    label="Favorite"
                    size="small"
                    color="warning"
                    variant="filled"
                  />
                )}
                {hasFollowUps && (
                  <Chip
                    icon={<ChatIcon />}
                    label={`${question.followUps.length} follow-up${question.followUps.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {hasFeedback && (
                  <Chip
                    icon={<FeedbackIcon />}
                    label={`${question.feedback.rating} star${question.feedback.rating !== 1 ? 's' : ''}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Question preview with search highlighting */}
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  cursor: 'pointer',
                  mb: 1
                }}
                onClick={() => toggleExpansion(question.id)}
              >
                {localSearchQuery ?
  highlightSearchTerms(question.questionText, localSearchQuery) :
  question.questionText
}
              </Typography>
              {/* Enhanced metadata row */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={statusDisplay.label}
                  size="small"
                  color={statusDisplay.color}
                  variant="filled"
                  icon={statusDisplay.icon}
                />

                {hasAttachment && (
                  <Chip
                    icon={<AttachFileIcon />}
                    label="Has Attachment"
                    size="small"
                    variant="outlined"
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 14 }} />
                  {formatTimestamp(question.createdAt)}
                </Typography>
              </Box>
            </Box>

            {/* Action Icons */}
            <Box display="flex" alignItems="center" gap={0.5}>
              {/* Favorite Toggle */}
              <Tooltip title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}>
                <IconButton
                  onClick={() => handleFavoriteToggle(question.id, isFavorited)}
                  sx={{
                    color: isFavorited ? 'warning.main' : 'text.secondary',
                    '&:hover': {
                      color: 'warning.main',
                      backgroundColor: 'rgba(255, 152, 0, 0.08)'
                    }
                  }}
                  aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isFavorited ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>

              {/* Share Button */}
              <Tooltip title="Share via Email">
                <IconButton
    onClick={(e) => {
        e.stopPropagation();
        handleShareItem(question);
    }}
    size="small"
    disabled={clientIsActive === false}
>
                  <ShareIcon fontSize="small" />
</IconButton>
</Tooltip>
{/* Export Single Item Button */}
<Tooltip title="Export to Word">
  <IconButton
    onClick={(e) => {
      e.stopPropagation();
      handleSingleItemExport(question);
    }}
    size="small"
  >
    <FileDownloadIcon fontSize="small" />
  </IconButton>
</Tooltip>
{/* NEW: Asana Button */}
<Tooltip title={isAsanaAvailableForItem(question) ? "Create Task in Asana" : "Connect an Asana account to enable"}>
  <span> {/* Span is needed to allow tooltip to show on a disabled button */}
    <IconButton
      size="small"
      disabled={!isAsanaAvailableForItem(question)}
      onClick={() => handleOpenAsanaModal(question)}
    >
      <PlaylistAddCheckIcon fontSize="small" />
    </IconButton>
  </span>
</Tooltip>
{/* Expand/Collapse Button */}
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton
                  onClick={() => toggleExpansion(question.id)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Expandable Content */}
          <Collapse in={isExpanded} timeout="auto">
            <Divider sx={{ mb: 2 }} />

            {/* Answer Section */}
            {question.answer && (
  <Box mb={2}>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>
      Answer:
    </Typography>
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: 'grey.50',
        borderColor: 'grey.200'
      }}
    >
      <Box sx={{ 
        fontSize: '1rem', 
        lineHeight: 1.7,
        '& p': { margin: 0, marginBottom: '0.5em' },
        '& p:last-child': { marginBottom: 0 },
        '& ul, & ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
        '& li': { marginBottom: '0.25em' },
        '& strong': { fontWeight: 600 },
        '& table': { width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' },
        '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
        '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 },
        '& code': { backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '3px', fontSize: '0.9em', fontFamily: 'monospace' }
      }}>
        <ReactMarkdown 
  remarkPlugins={[remarkGfm]}
  components={getHighlightedMarkdownComponents(localSearchQuery)}
>
  {question.answer}
</ReactMarkdown>
      </Box>
    </Paper>
  </Box>
)}

            {/* P2: Review Status Panel for Q&A */}
            <Box mb={2}>
              <ArtifactReviewPanel
                artifactType="question"
                artifactId={question.id}
                currentStatus={question.reviewStatus}
                reviewedByEmail={question.reviewedByEmail}
                reviewedAt={question.reviewedAt}
                reviewNotes={question.reviewNotes}
                reviewHistory={question.reviewHistory}
              />
            </Box>

            {/* User Notes Section */}
            <Box mb={2}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>
                Your Notes:
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.200'
                }}
              >
                {question.userNotes ? (
                  <Typography variant="body1" sx={{ fontSize: '1rem', whiteSpace: 'pre-wrap' }}>
                    {localSearchQuery ?
  highlightSearchTerms(question.userNotes, localSearchQuery) :
  question.userNotes
}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No notes added yet.
                  </Typography>
                )}
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditNote(question)}
                  sx={{ mt: 1 }}
                >
                  {question.userNotes ? 'Edit Note' : 'Add Note'}
                </Button>
              </Paper>
            </Box>
                   {question.sources && question.sources.length > 0 && (
    <Box mb={2}>
        <Box
          onClick={() => toggleSourcesExpansion(question.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 1,
            '&:hover': {
              opacity: 0.7
            }
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '1.1rem', mr: 1 }}>
            Sources:
          </Typography>
          <ExpandMoreIcon
            sx={{
              transform: expandedSources.has(question.id) ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          />
        </Box>
        <Collapse in={expandedSources.has(question.id)} timeout="auto">
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2,
              backgroundColor: 'grey.50',
              borderColor: 'grey.200'
            }}
          >
            <Stack spacing={1}>
              {question.sources.map((source, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: '0.9rem' }}>
                  <a href={source} target="_blank" rel="noopener noreferrer">
                    {source}
                  </a>
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Collapse>
    </Box>
)}
            {/* File Attachment */}
            {hasAttachment && (
              <Box mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  href={question.fileUrl || question.attachmentURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                >
                  View Attachment
                </Button>
              </Box>
            )}

            {/* Follow-up Questions */}
            {hasFollowUps && renderFollowUps(question.followUps, 'question', question.id)}

             {/* Action Buttons */}
            <Box display="flex" gap={1} mt={2} flexWrap="wrap">
              {canAskFollowUp && (
                <Button
                  variant="outlined"
                  startIcon={<ReplyIcon />}
                  size="small"
                  onClick={() => openFollowUpDialog(question)}
                >
                  Ask Follow-Up
                </Button>
              )}

              {canLeaveFeedback && (
                <Button
                  variant="outlined"
                  startIcon={<FeedbackIcon />}
                  size="small"
                  onClick={() => openFeedbackDialog(question)}
                  color={hasFeedback ? 'secondary' : 'primary'}
                >
                  {hasFeedback ? 'Update Feedback' : 'Leave Feedback'}
                </Button>
              )}

              {/* NEW: Generate Decision Memo button for Q&A */}
              {question.status === 'answered' && (
                <Button
                  variant="outlined"
                  startIcon={<ArticleIcon />}
                  size="small"
                  onClick={() => handleGenerateDecisionMemo(
                    question.id, 
                    null, 
                    question.questionText, // CORRECTED: was question.question
                    'question'
                  )}
                  disabled={clientIsActive === false}
                  sx={{ textTransform: 'none' }}
                >
                  Generate Decision Memo
                </Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2, fontSize: '1rem' }}>
          Loading questions...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Enhanced Search and Filter Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
    {/* Search Field */}
    <Grid item xs={12} md={4}>
        <SearchInput 
            onSearch={(value) => setLocalSearchQuery(value)} 
            onClear={() => setLocalSearchQuery('')} 
        />
    </Grid>
    {/* Date From */}
    <Grid item xs={6} md={2}>
        <TextField
            label="Date From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
        />
    </Grid>
    {/* Date To */}
    <Grid item xs={6} md={2}>
        <TextField
            label="Date To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
        />
    </Grid>
    {/* Clear Dates Button */}
    <Grid item xs={12} sm={6} md={1}>
        <Button variant="outlined" size="medium" onClick={clearDateFilters} disabled={!dateFrom && !dateTo} fullWidth sx={{height: '100%'}}>
            Clear
        </Button>
    </Grid>
    {/* Sort Dropdown */}
    <Grid item xs={6} sm={3} md={2}>
        <FormControl size="small" fullWidth>
            <InputLabel>Sort by</InputLabel>
            <Select
                value={localSortOption}
                onChange={(e) => setLocalSortOption(e.target.value)}
                label="Sort by"
            >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="favorites">Favorites First</MenuItem>
            </Select>
        </FormControl>
    </Grid>
    {/* NEW: Filter by Type Dropdown */}
    <Grid item xs={6} sm={3} md={2}>
        <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
            >
                <MenuItem value="all">All Content</MenuItem>
                <MenuItem value="qa_only">Q&A Only</MenuItem>
                <MenuItem value="updates_only">SavvaiSignals Only</MenuItem>
            </Select>
        </FormControl>
    </Grid>
    {/* MOVED: Export Button now on its own line, aligned right */}
    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
         <Button
            variant="contained"
            size="medium"
            onClick={() => setExportModalOpen(true)}
            disabled={processedItems.length === 0}
        >
            Configure & Export
        </Button>
    </Grid>
</Grid>

{/* Time-box indicator */}
{!showAllHistory && !dateFrom && !dateTo && (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    mb: 2, 
    p: 1.5, 
    bgcolor: 'grey.100', 
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'grey.300'
  }}>
    <Typography variant="body2" color="text.secondary">
      Showing last 90 days of activity
    </Typography>
    <Button 
      size="small" 
      onClick={() => setShowAllHistory(true)}
      sx={{ ml: 2 }}
    >
      Show All History
    </Button>
  </Box>
)}

{showAllHistory && !dateFrom && !dateTo && (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    mb: 2, 
    p: 1.5, 
    bgcolor: 'info.50', 
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'info.200'
  }}>
    <Typography variant="body2" color="text.secondary">
      Showing complete history
    </Typography>
    <Button 
      size="small" 
      onClick={() => setShowAllHistory(false)}
      sx={{ ml: 2 }}
    >
      Show Last 90 Days
    </Button>
  </Box>
)}

        {/* Results summary - only show when searching or filtering */}
{(localSearchQuery || dateFrom || dateTo) && (
  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
      {processedItems.length} item{processedItems.length !== 1 ? 's' : ''} found
      {localSearchQuery && (
        localSearchQuery.length > 15 ? ' for the selected source thread' : ` for "${localSearchQuery}"`
      )}
      {(dateFrom || dateTo) && (
        <span> • Filtered by date: {dateFrom || 'any'} to {dateTo || 'any'}</span>
      )}
    </Typography>
    {showLoadingNote && (
      <Typography variant="caption" color="info.main" sx={{ fontStyle: 'italic' }}>
        Loading complete history...
      </Typography>
    )}
  </Box>
)}
      </Paper>

      {/* Questions List */}
      {processedItems.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.100' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.2rem' }}>
            {localSearchQuery || dateFrom || dateTo ? 'No questions match your filters' : 'No questions yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            {localSearchQuery || dateFrom || dateTo
              ? 'Try adjusting your search terms or date range to see more questions.'
              : clientId === 'ALL'
                ? 'Questions will appear here once you or your clients submit them.'
                : 'Submit your first question using the form on the left.'
            }
          </Typography>
        </Paper>
      ) : (
  <Box>
    {processedItems.map(item => {
      if (item.dataType === 'question') {
        return renderQuestionCard(item);
      } else if (item.dataType === 'update') {
        return renderBusinessUpdateCard(item);
      }
      return null;
    })}
  </Box>
)}
      {/* Follow-up Dialog */}
      <Dialog
        open={followUpDialogOpen}
        onClose={closeFollowUpDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Ask Follow-Up Question
          {selectedQuestionForFollowUp && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Regarding: "{selectedQuestionForFollowUp.questionText?.substring(0, 100)}..."
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
  <TextField
    autoFocus
    multiline
    rows={4}
    fullWidth
    label="Your follow-up question"
    inputRef={followUpRef}
    defaultValue=""
    placeholder="What would you like to know more about?"
    sx={{ mt: 1 }}
  />
</DialogContent>
        <DialogActions>
          <Button
  onClick={handleFollowUpSubmit}
  variant="contained"
  disabled={submittingFollowUp}
>
  Submit
</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={closeFeedbackDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
  {selectedItemForFeedback?.feedback ? 'Update Feedback' : 'Leave Feedback'}
  {selectedItemForFeedback && (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Regarding: "{(selectedItemForFeedback.questionText || selectedItemForFeedback.title)?.substring(0, 100)}..."
    </Typography>
  )}
</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              How would you rate this answer?
            </Typography>
            <Rating
              value={feedbackRating}
              onChange={(event, newValue) => setFeedbackRating(newValue)}
              size="large"
              sx={{ mb: 2 }}
            />
            <TextField
              multiline
              rows={3}
              fullWidth
              label="Additional comments (optional)"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What did you think of this answer? Any suggestions?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFeedbackDialog}>Cancel</Button>
          <Button
            onClick={handleFeedbackSubmit}
            variant="contained"
            disabled={feedbackRating === 0 || submittingFeedback}
          >
            {submittingFeedback ? <CircularProgress size={20} /> : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Modal */}
      {selectedItemForShare && (
        <ShareModal
          open={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedItemForShare(null);
          }}
          item={selectedItemForShare}
          itemType={selectedItemForShare.dataType === 'update' ? 'update' : 'question'}
          followUps={selectedItemForShare.followUps || []}
          onShare={handleShare}
          user={user}
        />
      )}

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Your private note"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
  <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
  <Button onClick={handleSaveNote} variant="contained">
    Save Note
  </Button>
</DialogActions>
      </Dialog>
      
      {/* Export Configuration Modal */}
      <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Export</DialogTitle>
        <DialogContent>
          {/* Export Format Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              label="Export Format"
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <MenuItem value="csv">CSV (.csv)</MenuItem>
              <MenuItem value="word">Word Document (.docx)</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select which fields to include in your export:
          </Typography>

          <Grid container spacing={0}>
    {/* Q&A Section */}
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, width: '100%' }}>Q&A Fields</Typography>
    <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={exportFields.questionId} onChange={(e) => setExportFields(prev => ({...prev, questionId: e.target.checked}))}/>} label="Question ID" />
        <FormControlLabel control={<Checkbox checked={exportFields.clientName} onChange={(e) => setExportFields(prev => ({...prev, clientName: e.target.checked}))}/>} label="Client Name" />
        <FormControlLabel control={<Checkbox checked={exportFields.questionText} onChange={(e) => setExportFields(prev => ({...prev, questionText: e.target.checked}))}/>} label="Question Text" />
        <FormControlLabel control={<Checkbox checked={exportFields.answerText} onChange={(e) => setExportFields(prev => ({...prev, answerText: e.target.checked}))}/>} label="Answer Text" />
    </Grid>
    <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={exportFields.dateCreated} onChange={(e) => setExportFields(prev => ({...prev, dateCreated: e.target.checked}))}/>} label="Date Created" />
        <FormControlLabel control={<Checkbox checked={exportFields.feedbackRating} onChange={(e) => setExportFields(prev => ({...prev, feedbackRating: e.target.checked}))}/>} label="Feedback Rating" />
        <FormControlLabel control={<Checkbox checked={exportFields.feedbackText} onChange={(e) => setExportFields(prev => ({...prev, feedbackText: e.target.checked}))}/>} label="Feedback Comments" />
        <FormControlLabel control={<Checkbox checked={exportFields.userNotes} onChange={(e) => setExportFields(prev => ({...prev, userNotes: e.target.checked}))}/>} label="User Notes" />
    </Grid>
    <Grid item xs={12}>
        <FormControlLabel control={<Checkbox checked={exportFields.includeFollowUps} onChange={(e) => setExportFields(prev => ({...prev, includeFollowUps: e.target.checked}))}/>} label="Include All Follow-up Details" />
    </Grid>

    <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

    {/* Business Update Section */}
    <Typography variant="subtitle2" sx={{ mb: 1, width: '100%' }}>SavvaiSignals Fields</Typography>
    <Grid item xs={12} sm={6}>
    <FormControlLabel control={<Checkbox checked={exportFields.bu_title} onChange={(e) => setExportFields(prev => ({...prev, bu_title: e.target.checked}))}/>} label="Title" />
    <FormControlLabel control={<Checkbox checked={exportFields.bu_content} onChange={(e) => setExportFields(prev => ({...prev, bu_content: e.target.checked}))}/>} label="Content" />
</Grid>
<Grid item xs={12} sm={6}>
    <FormControlLabel control={<Checkbox checked={exportFields.bu_sources} onChange={(e) => setExportFields(prev => ({...prev, bu_sources: e.target.checked}))}/>} label="Sources" />
</Grid>
</Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={isExporting}
          >
            {isExporting ? <CircularProgress size={20} /> : `Export ${exportFormat === 'csv' ? 'CSV' : 'Document'}`}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Business Update Follow-up Dialog */}
<Dialog open={buFollowUpDialogOpen} onClose={() => setBuFollowUpDialogOpen(false)} maxWidth="sm" fullWidth>
    <DialogTitle>
        Ask Follow-Up on SavvaiSignals
        {selectedUpdateForFollowUp && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Regarding: "{selectedUpdateForFollowUp.title?.substring(0, 100)}..."
            </Typography>
        )}
    </DialogTitle>
    <DialogContent>
        <TextField
  autoFocus
  multiline
  rows={4}
  fullWidth
  label="Your follow-up question"
  inputRef={buFollowUpRef}
  defaultValue=""
  placeholder="What would you like to know more about?"
  sx={{ mt: 1 }}
/>
    </DialogContent>
    <DialogActions>
        <Button onClick={() => setBuFollowUpDialogOpen(false)}>Cancel</Button>
       <Button
  onClick={handleBusinessUpdateFollowUpSubmit}
  variant="contained"
  disabled={submittingBuFollowUp}
>
  {submittingBuFollowUp ? <CircularProgress size={20} /> : 'Submit Follow-Up'}
</Button>
    </DialogActions>
</Dialog>

  {/* NEW: Asana Task Creation Modal */}
  {selectedItemForAsana && (
    <CreateAsanaTaskModal
  open={isAsanaModalOpen}
  onClose={() => setIsAsanaModalOpen(false)}
  item={selectedItemForAsana}
  connectionContext={selectedItemForAsana?.asanaContext}
/>
  )}
</Box>
);
};

export default ExistingQuestions;
